import React, {useContext, useEffect, useRef, useState} from 'react';
import DefaultLayout from 'src/components/Layout/Default';
import withLayout from 'src/components/Layout/WithLayout';
import {BottomSpacer, OrderedListWrapper, ParagraphSpacer, ParagraphWrapper} from '../../components/StyledComponents';
import {Button, Col, ComponentLoader, ErrorSummary, Row, tokens} from '@snsw-gel/react';
import {useHistory} from 'react-router-dom';
import {ButtonsContainer, scrollToTop, useApplicationContext} from '@gaas/gaas-react-component-library';
import {FormSteps} from '../../config/formSteps';

import tollReliefConfig from '../../config/TollReliefConfig';
import ApiErrorAlert from './ApiErrorAlert';
import {
    EligibilityFailureScenario,
    saveRegistrationApi,
} from '../../services/api-services';
import {TollRegistrationContext} from '../../context/ApplicationContext';
import routes from '../../routes';
import RegistrationFlowTermsAndConditions, {
    RegistrationFlowTermsAndConditionsHandle,
    tAndCFormInputDefs,
} from '../../components/RegistrationFlowTermsAndConditions';
import {InputValueAndError} from '../../types/InputValueAndError';
import {
    collectErrorSummaryErrors,
    displayableTollAccountType,
    displayableTollProvider,
} from '../../utils/helpers';
import useTrRegistrationForm from '../../hooks/useTrRegistrationForm';
import useTrConfig from '../../hooks/useTrConfig';
import {ApplicationState} from '../../types/GaasAppState';
import {userInfo} from 'src/utils/sessionStorage';
import { EventData, generateEventData } from 'src/utils/ga-page-tracking';
import useStartTransaction from '../../hooks/useStartTransaction';
import {pxToRem} from '@snsw-gel/theming';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

/**
 * The review page for Sole Trader registrations
 */
// TODO remove this file after Tr2 decom
const ReviewPage: React.FC = () => {
    const trConfig = useTrConfig();

    const {isFeatureEnabled, isFeatureQueryLoading} = useBaseFeature();
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean>(false);

    const {productData, setProductData, setSuccessfullyLinkedAccountType} = useContext(TollRegistrationContext);
    const {tollAccountType} = productData;
    const {globalState} = useApplicationContext();
    const {supportingDocuments} = globalState as ApplicationState;

    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiErrorReason, setApiErrorReason] = useState<EligibilityFailureScenario | undefined>(undefined);
    const termsAndConditionsComponentRef = useRef<RegistrationFlowTermsAndConditionsHandle>(null);
    const [acceptNotifications, setAcceptNotifications] =
        useState<InputValueAndError<boolean>>(new InputValueAndError<boolean>(false, (input) => {
            return [input.hasValue()]
        }));
    const [agreeToSoleTraderTerms, setAgreeToSoleTraderTerms] =
        useState<InputValueAndError<boolean>>(new InputValueAndError<boolean>(false, (input) => {
            return [input.hasValue() && input.value]
        }));
    /** If the user has tried to continue. Used for displaying error summary or not. */
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    const transaction = useStartTransaction(history);
    const trRegistrationForm = useTrRegistrationForm();

    const formErrors = collectErrorSummaryErrors(tAndCFormInputDefs(trConfig,
        undefined, undefined, agreeToSoleTraderTerms));

    const errorSummaryRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if(!isFeatureQueryLoading && isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION)) {
            setIsTr2Decommissioned(true);
            history.push(routes.manageTollAccount.path);
            return;
        }
    }, [history, isFeatureQueryLoading, isFeatureEnabled]);

    useEffect(() => {
        if (apiErrorReason !== undefined) {
            scrollToTop(); // TODO: set focus to api error, just using scroll for now
        }
    }, [apiErrorReason]);

    // If either checkboxes change, update product data
    useEffect(() => {
        if (
            acceptNotifications.value !== productData.acceptNotifications ||
            agreeToSoleTraderTerms.value !== productData.agreeTerms?.agreeToTr2SoleTraderTerms
        ) {
            setProductData(
                {
                    ...productData,
                    agreeTerms: {
                        agreeToTr2SoleTraderTerms: agreeToSoleTraderTerms.value ?? false,
                    },
                    acceptNotifications: acceptNotifications.value ?? false,
                },
            );
        }
    }, [acceptNotifications, agreeToSoleTraderTerms, setProductData, productData]);

    const callApply = async () => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        const potentialFailure = await saveRegistrationApi(
            trRegistrationForm.data(
                {
                    ...productData, agreeTerms: {
                        // Explicitly overriding any personal terms they could have accepted on a different page.
                        ...productData.agreeTerms,
                        agreeToTr2PersonalTerms: false,
                        agreeToTr3PersonalTerms: false,
                    },
                },
            ),
        );

        if (potentialFailure === null) { // success
            // Set this so MTA can display success message
            setSuccessfullyLinkedAccountType(tollAccountType);
            // Clear transaction context
            transaction.clear();
            // Clear the product data
            setProductData({});
            // Back to MTA
            history.push(routes.manageTollAccount.path);
        } else {
            setApiErrorReason(potentialFailure);
        }

        setIsSubmitting(false);
    };

    const onSaveRegistration = async () => {
        setHasAttemptedContinue(true);
        if (termsAndConditionsComponentRef.current?.validate()) {
            await callApply();
        } else {
            errorSummaryRef.current?.focus(); // sets focus to error summary
        }
    }

    const onEdit = () => {
        scrollToTop();
        history.push(routes.supportingDocumentsPage.path);
    }

    const userInfoObj = userInfo();
    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            // Simulating data for the event
            const eventData: EventData = {
                page_title: 'Link toll account',
                transaction_info: {
                    transaction_step: 'Review',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    return (
        <>
            <ComponentLoader active={isSubmitting || isFeatureQueryLoading || isTr2Decommissioned} fullPage label={tollReliefConfig.labels.componentLoader}/>

            {/* Error summary - for frontend errors */}
            {hasAttemptedContinue && formErrors.length !== 0 && <ErrorSummary errors={formErrors} ref={errorSummaryRef}/>}

            {apiErrorReason !== undefined && <ApiErrorAlert errorReason={apiErrorReason}/>}

            <p>All fields to be completed unless marked (optional).</p>
            <h2>Review</h2>
            <h3>{displayableTollAccountType(tollAccountType)} toll account details</h3>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Toll provider
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{displayableTollProvider(productData.tollAccountDetailsPageData?.tollProvider)}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Toll account number
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{productData.tollAccountDetailsPageData?.tollAccountNumber}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Licence plate number
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{productData.tollAccountDetailsPageData?.lpn}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Toll account email address:
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{productData.tollAccountDetailsPageData?.tollAccountEmail}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <ParagraphSpacer/>
            <h3>Sole trader details</h3>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Is your business operating as a ride-share or taxi business?:
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>No</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            Business name:
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{productData.soleTraderDetailData?.businessName}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <BottomSpacer>
                        <ParagraphWrapper>
                            ABN or ACN:
                        </ParagraphWrapper>
                        <ParagraphWrapper>
                            <strong>{productData.soleTraderDetailData?.abn}</strong>
                        </ParagraphWrapper>
                    </BottomSpacer>
                </Col>
            </Row>
            <ParagraphSpacer/>
            <h3>Supporting details</h3>
            <Row>
                <Col span={8}>
                    {
                        supportingDocuments.documents !== undefined &&
                        supportingDocuments.documents.length > 0 && (
                            <>
                                <ParagraphWrapper>Evidence of business turnover:</ParagraphWrapper>
                                <OrderedListWrapper>
                                    {supportingDocuments.documents.map((file) => (
                                        <li key={file.id}><strong>{file.name}</strong></li>
                                    ))}
                                </OrderedListWrapper>
                            </>
                        )}
                </Col>
            </Row>
            <ParagraphSpacer/>
            <Row>
                <Col span={6}>
                    <Button theme='secondary' onClick={onEdit} id='supportingDocsEdit'>
                        Edit
                    </Button>
                </Col>
            </Row>
            <RegistrationFlowTermsAndConditions
                isSoleTraderRegistration={true}
                ref={termsAndConditionsComponentRef}
                setAcceptNotifications={setAcceptNotifications}
                acceptNotifications={acceptNotifications}
                setAgreeToTr2SoleTraderTerms={setAgreeToSoleTraderTerms}
                agreeToTr2SoleTraderTerms={agreeToSoleTraderTerms}
            />

            <ButtonsContainer>
                <Button
                    className='button-item'
                    id='submitButton'
                    onClick={onSaveRegistration}
                    data-testid='submitButton'
                >
                    Submit
                </Button>
            </ButtonsContainer>
            <Button
                style={{marginTop: `${pxToRem(tokens.spacing.sm)}`}}
                id='backButton'
                onClick={onEdit}
                theme='back'
            >
                Back
            </Button>
        </>);
}
export default withLayout(ReviewPage, DefaultLayout, {
    step: FormSteps.Review,
    headerTitle: 'Link toll account',
});
